import { Chip, type ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CommunicationChannel, CommunicationOutcome, FileKind, SendOperationStatus } from '../api/types';
import {
  communicationChannelLabelKeys,
  communicationOutcomeLabelKeys,
  fileKindLabelKeys,
  gateStatusLabel,
  operationStatusLabelKeys,
} from './statusLabels';

type ChipColor = ChipProps['color'];

const communicationOutcomeColors: Record<CommunicationOutcome, ChipColor> = {
  Accepted: 'success',
  Retried: 'warning',
  Rejected: 'error',
  Published: 'info',
  Failed: 'error',
  Dropped: 'warning',
};

/** How one exchange on the message channels ended: the verdict on a received message, or the fate of a sent one. */
export function CommunicationOutcomeChip({ outcome }: { outcome: CommunicationOutcome }) {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      variant={outcome === 'Retried' ? 'outlined' : 'filled'}
      color={communicationOutcomeColors[outcome]}
      label={t(communicationOutcomeLabelKeys[outcome])}
    />
  );
}

/** Which queue an exchange travelled on; outlined, because it labels rather than judges. */
export function CommunicationChannelChip({ channel }: { channel: CommunicationChannel }) {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      variant="outlined"
      color={channel === 'SubmitQueue' ? 'primary' : 'secondary'}
      label={t(communicationChannelLabelKeys[channel])}
    />
  );
}

const operationStatusColors: Record<SendOperationStatus, ChipColor> = {
  Queued: 'default',
  Submitted: 'info',
  Duplicate: 'warning',
  Rejected: 'error',
  Failed: 'error',
};

/**
 * `retrying` marks a queued operation whose delivery has already failed at least once and is still
 * being retried — work in hand rather than work not started, which the bare status cannot say.
 */
export function OperationStatusChip({
  status,
  retrying = false,
}: {
  status: SendOperationStatus;
  retrying?: boolean;
}) {
  const { t } = useTranslation();
  const waiting = status === 'Queued' && retrying;
  const label = t(operationStatusLabelKeys[status]);

  return (
    <Chip
      size="small"
      variant={status === 'Queued' ? 'outlined' : 'filled'}
      color={waiting ? 'warning' : operationStatusColors[status]}
      label={waiting ? `${label} (${t('status.retrying')})` : label}
    />
  );
}

const fileKindColors: Record<FileKind, ChipColor> = {
  BisDocument: 'primary',
  IsdocDocument: 'secondary',
  Attachment: 'default',
  InboundXml: 'info',
  InboundPdf: 'info',
  InboundJson: 'info',
  InboundAttachment: 'default',
};

export function FileKindChip({ kind }: { kind: FileKind }) {
  const { t } = useTranslation();

  return (
    <Chip size="small" variant="outlined" color={fileKindColors[kind]} label={t(fileKindLabelKeys[kind])} />
  );
}

/** The gate's own delivery states, which are open-ended — an unknown one is shown as-is. */
const gateStatusColors: Record<string, ChipColor> = {
  draft: 'default',
  new: 'default',
  ready_to_send: 'info',
  sent: 'info',
  accepted: 'success',
  completed: 'success',
  rejected: 'error',
  not_valid: 'error',
  failed_delivery: 'error',
};

export function GateStatusChip({ status }: { status: string | null }) {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      variant="outlined"
      color={(status ? gateStatusColors[status] : undefined) ?? 'default'}
      label={status ? gateStatusLabel(status, t) : '—'}
    />
  );
}
